import React, { useContext } from "react"
import { useTranslation } from "react-i18next"
import { IAppState } from "../../../app/appTypes"
import NameUser from "../../../shared/components/names/NameUser"
import TimeDateOfBirth from "../../../shared/components/time/TimeDateOfBirth"
import { TenantConfigContext } from "../../tenantConfig/contexts/TenantConfigContext"
import { MockStore } from "redux-mock-store"
import { IMeetingCase, MeetingCaseRecommendationStatus } from "../state/meetingCaseTypes"
import { getMeetingCaseById } from "../../../shared/selectors/meeting"
import { getCaseById, getLeadCareUserByCaseId } from "../../../shared/selectors/case"
import TextCaseType from "../../../shared/components/text/TextCaseType"
import { generatePath } from "react-router"
import { RoutePaths } from "../../../app/routes/Routes"
import TimeDefaultFormat from "../../../shared/components/time/TimeDefaultFormat"
import TargetDate from "../../case/components/details/TargetDate"
import LockCaseButton from "../../case/components/header/LockCaseButton"
import LoadingSpinner from "../../../shared/components/ui/LoadingSpinner"
import { useSelector } from "react-redux"
import PatientNameWithLock from "../../case/components/details/PatientNameWithLock"
import humps from "humps"

interface IProps {
  store?: MockStore
  isCondensed?: boolean
  meetingCaseId: string
  hasLink?: boolean
  hasCaseLink?: boolean
  renderLockCase?: boolean
}

const MeetingCaseDetails: React.FC<IProps> = (props: IProps): JSX.Element => {
  const { t } = useTranslation(["shared", "case"])

  const meetingCase: IMeetingCase = useSelector((state: IAppState) => getMeetingCaseById(state, props))
  const caseData = useSelector((state: IAppState) => getCaseById(state, { caseId: meetingCase.case }))
  const leadCareUser = useSelector((state: IAppState) => getLeadCareUserByCaseId(state, { caseId: meetingCase.case }))

  const { healthcareIdLabel, careProviderIdLabel } = useContext(
    TenantConfigContext
  )

  const meetingCaseDetailsPath = () => generatePath(RoutePaths.MEETING_CASE, {
    meetingCaseId: props.meetingCaseId
  })

  if (!caseData) {
    return (<LoadingSpinner />)
  }

  const caseDetailsPath = () => generatePath(RoutePaths.CASE, {
    caseId: caseData.id
  })

  const renderPatientName = (): JSX.Element => {
    const renderRecommendationLabel = (): JSX.Element => {
      const status = meetingCase.recommendationStatus
      if (!status) return null

      if (status == MeetingCaseRecommendationStatus.RECOMMENDED)
        return (
          <div className="text-sm text-white px-1 rounded bg-ch-green-300 ml-2 flex content-center">
            {t("case:recommended")}
          </div>
        )
      if (status == MeetingCaseRecommendationStatus.DEFERRED)
        return (
          <div className="text-sm text-white px-1 rounded bg-ch-blue-500 ml-2 flex content-center">
            {t("case:deferred")}
          </div>
        )
      if (status == MeetingCaseRecommendationStatus.RECOMMENDATION_PENDING)
        return (
          <div className="text-sm text-white px-1 rounded bg-ch-yellow-500 ml-2 flex content-center">
            {t("case:recommendationPending")}
          </div>
        )
      return null
    }

    let link: string = ""
    if (props.hasLink) {
      link = meetingCaseDetailsPath()
    } else if (props.hasCaseLink) {
      link = caseDetailsPath()
    }

    return (
      <li className="flex flex-row items-baseline">
        <PatientNameWithLock case={caseData} hasLink={!!props.hasCaseLink || !!props.hasLink} link={link} >
          {renderRecommendationLabel()}
        </PatientNameWithLock>
      </li>
    )
  }

  const renderGender = (): JSX.Element => {
    if (!caseData.gender) return null

    const translationKey = humps.camelize(caseData.gender)
    return (
      <li>
        {t("case:gender")}: {t(`shared:${translationKey}`)}
      </li>
    )
  }

  const renderDateOfBirth = (): JSX.Element => {
    return (
      <li>
        {t("case:dateOfBirth")}:{" "}
        <TimeDateOfBirth utcTime={caseData.dateOfBirth} />
      </li>
    )
  }

  const renderPatientHealthcareId = (): JSX.Element => {
    return (
      <li>
        {healthcareIdLabel}: {caseData.healthcareId}
      </li>
    )
  }

  const renderPatientCareProviderId = (): JSX.Element => {
    return (
      <li>
        {careProviderIdLabel}: {caseData.careProviderId}
      </li>
    )
  }

  const renderCaseType = (): JSX.Element => {
    return (
      <li>
        {t("case:type")}:{" "}
        <TextCaseType caseType={caseData.caseType} />
      </li>
    )
  }

  const renderLeadCare = (): JSX.Element => {
    return (
      <li>
        {t("case:leadCare")}:{" "}
        <NameUser
          firstName={leadCareUser.firstName}
          lastName={leadCareUser.lastName}
          isPending={leadCareUser.pending}
        />
      </li>
    )
  }

  const renderClassification = (): JSX.Element => {
    return (
      <li>
        {t("shared:classification")}: {caseData.classification}
      </li>
    )
  }

  const renderSourceName = (): JSX.Element => {
    return (
      <li>
        {t("case:source")}: {caseData.sourceName}
      </li>
    )
  }

  const renderSignedOffBy = (): JSX.Element => {
    if (!caseData.signedOffBy) return null
    return (
      <li>
        {t("case:signedOffBy")}: {caseData.signedOffBy}
      </li>
    )
  }

  const renderLastUpdated = (): JSX.Element => {
    return (
      <li>
        {t("case:lastUpdated")}: <TimeDefaultFormat utcTime={caseData.updatedAt} />
      </li>
    )
  }

  const renderCondensedDetails = (): JSX.Element => {
    return (
      <ul>
        {renderPatientName()}
        {renderDateOfBirth()}
        {renderPatientHealthcareId()}
        {renderPatientCareProviderId()}
      </ul>
    )
  }

  const renderFullDetails = (): JSX.Element => {
    return (
      <div className="flex w-full">
        <div className="flex-grow flex-1 lg:grid grid-cols-2 gap-3">
          <ul className="col-span-1">
            {renderPatientName()}
            {renderGender()}
            {renderDateOfBirth()}
            {renderPatientHealthcareId()}
            {renderPatientCareProviderId()}
            {renderSourceName()}
          </ul>

          <ul className="col-span-1">
            {renderLeadCare()}
            {renderCaseType()}
            {renderClassification()}
            {renderLastUpdated()}
            <TargetDate caseId={caseData.id} />
            {renderSignedOffBy()}
          </ul>
        </div>
        {props.renderLockCase ? <LockCaseButton caseId={meetingCase.case} /> : null}
      </div>
    )
  }

  return props.isCondensed ? renderCondensedDetails() : renderFullDetails()
}

export default MeetingCaseDetails
